import type { RegisterFormSubmitFn } from '../../form/RegisterForm/RegisterFormBase/RegisterFormBase';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import useLocalStorageState from 'use-local-storage-state';

import { postLogin, postRegisterAppSumo } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import { AppSumoLogo, FlotiqLogo } from '../../images/shapes';

import RegisterForm from '../../form/RegisterForm/RegisterForm';
import Authentication from '../../hoc/Authentication/Authentication';

const testId = 'register-appsumo';

const RegisterAppSumo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toastRef = useRef<string>(null);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [, setUser] = useLocalStorageState('cms.user');

  useEffect(() => {
    if (toastRef.current) {
      toast.remove(toastRef.current);
      toastRef.current = null;
    }
    if (!isTokenValid) {
      toastRef.current = toast.error(t('AppSumo.TokenNotValid'));
    }
  }, [isTokenValid, t]);

  const encodedToken = useMemo(() => {
    const tokenParam = searchParams.get('activationToken');
    if (!tokenParam) {
      setIsTokenValid(false);
      return '';
    }
    try {
      return window.atob(tokenParam);
    } catch {
      setIsTokenValid(false);
      return '';
    }
  }, [searchParams]);

  const activationTokenArray = useMemo(
    () => encodedToken.split(':'),
    [encodedToken],
  );
  const email = useMemo(() => activationTokenArray[0], [activationTokenArray]);
  const token = useMemo(() => activationTokenArray[1], [activationTokenArray]);

  const handleLogin = useCallback(
    async (plainPassword: string, email: string) => {
      try {
        const { body, status, ok } = await postLogin(plainPassword, email);
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        setUser(body);
        navigate('/');
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(
            error.message
              ? error.message
              : t('Global.ErrorInvalidLoginOrPassword'),
          );
        }
      }
    },
    [setUser, navigate, t],
  );

  const onSubmit = useCallback<RegisterFormSubmitFn>(
    async (values) => {
      const userData = { ...values };
      try {
        const { body, status, ok } = await postRegisterAppSumo(token, userData);
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('AppSumo.CreatedAccount'));
        await handleLogin(values.plainPassword, values.email);
        return null;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return {
            general: t('Form.CommunicationErrorMessage'),
          };
        }
        toast.error(
          error.message ? error.message : t('AppSumo.CouldntCreateAccount'),
        );
        return {
          general: t('AppSumo.CouldntCreateAccount'),
          ...error.errors,
        };
      }
    },
    [t, handleLogin, token],
  );

  return (
    <>
      <title>{t('Global.RegisterToFlotiq')} - AppSumo</title>
      <Authentication
        headerText={t('AppSumo.WelcomeSumoLing')}
        paragraphText={t('Global.AccountAlmostReady')}
        {...getTestProps(testId, 'authentication', 'testId')}
      >
        <RegisterForm
          onSubmit={onSubmit}
          prefilledEmail={email}
          {...getTestProps(testId, 'form', 'testId')}
        />

        <div className="flex justify-center items-center mt-8 gap-5">
          <FlotiqLogo className="h-8" />
          <span className="text-slate-500">with</span>
          <Link to="https://appsumo.com" target="_blank" rel="noreferrer">
            <AppSumoLogo
              className="h-4 dark:invert"
              {...getTestProps(testId, 'appsumo-logo')}
            />
          </Link>
        </div>
      </Authentication>
    </>
  );
};

export default RegisterAppSumo;
